import api from '@anm/api';
import { SignUpData } from '@anm/api/modules/auth';
import asyncEntity from '@anm/helpers/redux/asyncEntity';
import { call, cancel, fork, put, select, take } from '@anm/helpers/saga/effects';
import { takeType } from '@anm/helpers/saga/typesafe-actions';
import { Task } from '@redux-saga/types';

import userNotifications, { CONFIRM_APP_SUMO_EMAIL } from '../../../helpers/notifications/userNotifications';
import routes from '../../../routes';
import { appMetaSelectors } from '../appMeta';
import { authActions } from '../auth';
import * as notification from '../notification';
import { userActions, userSelectors } from '../user';

import { appSumoActions } from '.';
import { APP_SUMO_SUCCESS_STACKED } from './../../../helpers/notifications/userNotifications';
import { AppSumoSignUpData, CouponCodes } from './types';

const { Router } = routes;

const activateCouponRequest = async ({ code, code2 }: CouponCodes) => {
  const res = await api().subscription.activateCoupon({ code });
  code2 && (await api().subscription.activateCoupon({ code: code2 }));

  return res;
};

export const upgradeAsync = asyncEntity(appSumoActions.upgradeAsync, () => api().subscription.upgradeAppSumo());
export const activateCouponAsync = asyncEntity(appSumoActions.activateCouponAsync, activateCouponRequest);

const checkCouponRequest = (code: string) => api().subscription.checkCoupon({ code });

export const checkCouponAsync = asyncEntity(appSumoActions.checkCouponAsync, checkCouponRequest);

function* signUpAppSumo(formFields: Omit<AppSumoSignUpData, 'code'>) {
  const { product } = yield* select(appMetaSelectors.selectAppMeta);
  const signUpData: SignUpData = {
    ...formFields,
    product,
    type: 'EMAIL',
    entry: 'APPSUMO'
  };

  yield put(authActions.signup(signUpData));
}

function* showEmailConfirmNotification() {
  const user = yield* select(userSelectors.selectUserProfile);

  const confirmEmailAction = notification.activate(
    userNotifications(CONFIRM_APP_SUMO_EMAIL, undefined, { email: user?.email })
  );

  yield put(confirmEmailAction);
}

function* showAppSumoStackedNotification() {
  const appSumoStackedAction = notification.activate(userNotifications(APP_SUMO_SUCCESS_STACKED));

  yield put(appSumoStackedAction);
}

const redirectAfterSignUp = () => {
  Router.pushRoute('video-templates');
};

const redirectAfterActivationCoupon = () => {
  Router.pushRoute('my-projects');
};

function* signUpAndActivateCoupon({ code, code2, ...signUpData }: AppSumoSignUpData) {
  try {
    yield* put(appSumoActions.changeRedirectPending(true));

    yield* call(signUpAppSumo, signUpData);

    yield* take(takeType(authActions.authFinished));
    yield* fork(activateCouponAsync, { code, code2 });

    yield* take(takeType(appSumoActions.activateCouponAsync.success));
    yield* call(showEmailConfirmNotification);

    yield* put(userActions.getUser());
    yield* take(takeType(userActions.getUserAsync.success));
    yield* call(redirectAfterSignUp);
  } finally {
    yield* put(appSumoActions.changeRedirectPending(false));
  }
}

function* activateCoupon({ code, code2 }: CouponCodes) {
  yield* fork(activateCouponAsync, { code, code2 });
  yield* take(takeType(appSumoActions.activateCouponAsync.success));
  yield* call(showAppSumoStackedNotification);
  yield* put(userActions.getUser());
}

function* activateCouponWithRedirect(codes: CouponCodes) {
  try {
    yield* put(appSumoActions.changeRedirectPending(true));

    yield* call(activateCoupon, codes);
    yield* take(takeType(userActions.getUserAsync.success));
    yield* call(redirectAfterActivationCoupon);
  } finally {
    yield* put(appSumoActions.changeRedirectPending(false));
  }
}

function* watchSignUpAppSumo() {
  while (true) {
    const { payload } = yield* take(takeType(appSumoActions.signupAppSumo));

    yield* call(signUpAndActivateCoupon, payload);
  }
}

function* refreshUser() {
  yield* take(takeType(appSumoActions.upgradeAsync.success));
  yield* put(userActions.getUser());
}

function* watchUpgrade() {
  while (true) {
    yield* take(takeType(appSumoActions.upgrade));

    yield* fork(refreshUser);
    yield* call(upgradeAsync);
  }
}

const getActivateCouponWatcher = () => {
  let lastTask: Task;

  return function* watchActivateCoupon() {
    while (true) {
      const { payload: codes } = yield* take(takeType(appSumoActions.activateCoupon));

      if (lastTask) {
        cancel(lastTask);
      }

      lastTask = yield* fork(activateCoupon, codes);
    }
  };
};

function* watchActivateCouponWithRedirect() {
  while (true) {
    const { payload: codes } = yield* take(takeType(appSumoActions.activateCouponWithRedirect));

    yield* call(activateCouponWithRedirect, codes);
  }
}

function* watchCheckCoupon() {
  while (true) {
    const { payload: code } = yield* take(takeType(appSumoActions.checkCoupon));

    yield* call(checkCouponAsync, code, code);
  }
}

const appsumoSaga = () => [
  call(watchUpgrade),
  call(watchCheckCoupon),
  call(watchSignUpAppSumo),
  call(getActivateCouponWatcher()),
  call(watchActivateCouponWithRedirect)
];

export default appsumoSaga;
